import MeioJulgamentoProcesso, { MEIO_JULGAMENTO_PROCESSO } from '@/dominio/MeioJulgamentoProcesso';
import { MEIO_JULGAMENTO_SESSAO } from '@/dominio/MeioJulgamentoSessao';
import SituacaoJulgamentoProcessoBusiness from '@/dominio/SituacaoJulgamentoProcessoBusiness';
import { TIPO_REMESSA_JULGAMENTO_PROCESSO } from '@/dominio/TipoRemessaJulgamentoProcesso';
import ErrorUtil from '@/util/ErrorUtil';
import StringUtil from '@/util/StringUtil';
import styled from '@emotion/styled';
import Warning from '@mui/icons-material/Warning';
import {
    Avatar,
    Box,
    FormControl,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Popover,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import { deepOrange } from '@mui/material/colors';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '../../../util/Configuracao';
import PopoverSecretaria from '../../PopoverSecretaria';
import ReactComponent from '../../ReactComponent';
import DetalheRemessaJulgamento from '../DetalheRemessaJulgamento';
import IconeTipoRemessaJulgamentoFormRemessa from './IconeTipoRemessaJulgamentoFormRemessa';

dayjs.extend(customParseFormat);

const KeyframesBlinkTipoJulgamentoInconsistente = styled('span')({
    '@keyframes blink': {
        from: {
            opacity: 1
        },
        to: {
            opacity: 0.3
        }
    },
    animation: 'blink 1s',
    animationIterationCount: 'infinite'
});

/**
 * @desc Enum para níveis de criticidade do alerta.
 * @readonly
 * @constant
 * @enum {Object}
 */
// prettier-ignore
export const NIVEL_CRITICIDADE_ALERTA = {
    INFORMACAO:     { id: 'INFORMACAO',     descricao: 'IMPORTANTE' },
    AVISO:          { id: 'AVISO',          descricao: 'ATENÇÃO!' },
    INCONSISTENCIA: { id: 'INCONSISTENCIA', descricao: 'ATENÇÃO! INCONSISTÊNCIA DETECTADA.' }
};

export const MENSAGEM_INCOSISTENCIA = {
    PRESENCIAL_EM_SESSAO_EXCLUSIVAMENTE_VIRTUAL:
        'O meio de julgamento presencial, assinalado para este processo confirmado em pauta/sessão, é incompatível com a modalidade de sessão exclusivamente virtual. Verifique possível erro material na publicação da pauta.',
    REMETIDO_PARA_PRESENCIAL_VINCULADA_EM_SESSAO_EXCLUSIVAMENTE_VIRTUAL:
        'O meio de julgamento presencial (remetido), assinalado para este processo, é incompatível com a modalidade de sessão exclusivamente virtual. Comunique a equipe da SETIN/CDS para que o ajuste possa ser feito na base de dados.',
    VIRTUAL_EM_SESSAO_SEM_PERIODO_VIRTUAL:
        'O meio de julgamento virtual, assinalado para este processo, é incompatível com a sessão sem período virtual determinado. Verifique possível ajuste no tipo de julgamento do processo ou defina período virtual para sessão.',
    VIRTUAL_REMETIDO_JULGADO:
        'O meio de julgamento virtual (remetido) é incompatível com o resultado de julgamento. Processos remetidos em sessões exclusivamente virtuais deveriam ter um resultado de julgamento "NÃO JULGADO".'
};

const HABILITAR_EXIBIR_DETALHES_REMESSA = false;

class IconeTipoJulgamento extends ReactComponent {
    anchorEl = null;
    acabouDeFechar = false;
    popoverAberto = false;

    /** @type {object | null} */
    tipoRemessaPadrao = null;

    /** @type {boolean} */
    openCadastroRemessa = false;

    get idPopoverCadastroRemessa() {
        return this.openCadastroRemessa ? 'popover-edicao-remessa-tipo-julgamento' : undefined;
    }

    /** @type {string[]} */
    motivoRemessaPresencial = [];

    /** @type {Array} */
    mensagensInconsistencias = [];

    constructor(props) {
        super(props);

        makeObservable(this, {
            anchorEl: observable,
            acabouDeFechar: observable,
            popoverAberto: observable,
            tipoRemessaPadrao: observable,
            openCadastroRemessa: observable,
            idPopoverCadastroRemessa: computed,
            motivoRemessaPresencial: observable,
            mensagensInconsistencias: observable,
            registro: computed,
            codigoMeioJulgamentoSessao: computed,
            codigoMeioJulgamentoProcesso: computed,
            codigoMeioJulgamentoProcessoForm: computed,
            idTipoRemessaJulgamento: computed,
            motivoRemessaJulgamento: computed,
            isPermitidaAlteracao: computed,
            isPermitidaAlteracaoTipoJulgamento: computed,
            isPermitidaRemessa: computed,
            isPermitidaRemessaJulgamento: computed,
            sessaoSelecionada: computed,
            isSessaoFinalizada: computed,
            exibirDetalhes: computed,
            complementoTooltip: computed,
            isJulgamentoExclusivamenteVirtual: computed,
            sessaoSemPeriodoVirtualDefinido: computed,
            sessaoIdAssociada: computed,
            isProcessoEmPautaOuSessao: computed,
            temAdvComParticipacaoVirtual: computed,
            isOpcaoPresencialDesabilitada: computed,
            isOpcaoPresencialRemetidoDesabilitada: computed,
            isOpcaoVirtualDesabilitada: computed,
            isOpcaoVirtualRemetidoDesabilitada: computed,
            isProcessoRemetido: computed,
            isProcessoApreciado: computed,
            isProcessoJulgado: computed,
            titleVirtual: computed,
            titlePresencial: computed,
            titlePresencialRemetido: computed,
            titleVirtualRemetido: computed,
            titleAlertaImpossibilidadeAlteracao: computed,
            carregarOpcaoTipoJulgamentoNaoVirtual: computed,
            handleClose: action,
            handleClickIcone: action,
            inconsistencias: computed,
            handleChangeTipoJulgamento: action,
            deveAbrirMenuAlteracao: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get codigoMeioJulgamentoSessao() {
        if (this.sessaoSelecionada?.tipoJulgamento) {
            return this.sessaoSelecionada?.tipoJulgamento;
        }

        return '';
    }

    get codigoMeioJulgamentoProcesso() {
        return this.registro ? this.registro.tipoJulgamento : this.observableProps.tipoJulgamento;
    }

    get codigoMeioJulgamentoProcessoForm() {
        if (this.isProcessoRemetido) {
            return this.isJulgamentoExclusivamenteVirtual
                ? MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.codigo
                : MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo;
        }

        return this.codigoMeioJulgamentoProcesso;
    }

    get idTipoRemessaJulgamento() {
        return this.registro?.remessaJulgamento?.tipo?.id || '';
    }

    get motivoRemessaJulgamento() {
        return this.registro?.remessaJulgamento?.descricaoMotivo || '';
    }

    get isPermitidaAlteracao() {
        return this.observableProps.permiteAlterar && this.isPermitidaAlteracaoTipoJulgamento;
    }

    get isPermitidaRemessa() {
        return this.observableProps.permiteAlterar && this.isPermitidaRemessaJulgamento;
    }

    get isPermitidaAlteracaoTipoJulgamento() {
        return !!(this.registro && this.salaSessaoStore.isPermitidaAlteracaoTipoJulgamento(this.registro));
    }

    get isPermitidaRemessaJulgamento() {
        return !!(this.registro && this.salaSessaoStore.isPermitidaRemessa(this.registro));
    }

    get sessaoSelecionada() {
        return this.sessaoStore.sessaoSelecionada;
    }

    get isSessaoFinalizada() {
        return this.sessaoStore.isSessaoFinalizada;
    }

    get exibirDetalhes() {
        const detalhesRemessa = HABILITAR_EXIBIR_DETALHES_REMESSA && this.isProcessoRemetido;
        const {
            detalhesInconsistenciaJulgamentoPresencial,
            detalhesInconsistenciaRemetidoPresencialVinculadaEmSessaoExclusivamenteVirtual,
            detalhesInconsistenciaJulgamentoVirtual,
            detalhesInconsistenciaRemetidoJulgadoEmSessaoExclusivamenteVirtual
        } = this.inconsistencias;

        return (
            detalhesRemessa ||
            detalhesInconsistenciaJulgamentoPresencial ||
            detalhesInconsistenciaRemetidoPresencialVinculadaEmSessaoExclusivamenteVirtual ||
            detalhesInconsistenciaJulgamentoVirtual ||
            detalhesInconsistenciaRemetidoJulgadoEmSessaoExclusivamenteVirtual
        );
    }

    get deveAbrirMenuAlteracao() {
        return !!(this.isPermitidaAlteracao || this.isPermitidaRemessa || this.exibirDetalhes);
    }

    get complementoTooltip() {
        if (this.isPermitidaAlteracao || this.isPermitidaRemessa) {
            return ' [clique para alterar]';
        } else if (this.exibirDetalhes) {
            return ' [clique para detalhes]';
        }

        return '';
    }

    get isJulgamentoExclusivamenteVirtual() {
        return SituacaoJulgamentoProcessoBusiness.isProcessoPautadoEmJulgamentoExclusivamenteVirtual(
            this.codigoMeioJulgamentoSessao
        );
    }

    get sessaoSemPeriodoVirtualDefinido() {
        return (
            !this.sessaoSelecionada?.prazosPorSessao ||
            !this.sessaoIdAssociada ||
            !this.sessaoSelecionada.prazosPorSessao[this.sessaoIdAssociada]?.temPrazoDeVotacaoVirtual
        );
    }

    get sessaoIdAssociada() {
        if (this.registro?.sessaoId) {
            return this.registro.sessaoId;
        }

        // Para processos disponíveis, não há sessão cadastrada no processo, utilizar a selecionada na combo de pauta
        return this.pautaJulgamentoStore?.idPautaSelecionada;
    }

    get isProcessoEmPautaOuSessao() {
        return !!this.registro.sessaoId;
    }

    get temAdvComParticipacaoVirtual() {
        return this.salaSessaoStore.temPartipacaoVirtual(this.registro);
    }

    get isOpcaoPresencialDesabilitada() {
        return this.isSessaoFinalizada;
    }

    get isOpcaoPresencialRemetidoDesabilitada() {
        return this.isSessaoFinalizada || this.isProcessoRemetido;
    }

    get isOpcaoVirtualDesabilitada() {
        return this.isSessaoFinalizada || this.isProcessoRemetido;
    }

    get isOpcaoVirtualRemetidoDesabilitada() {
        return this.isSessaoFinalizada || this.isProcessoRemetido;
    }

    get isProcessoRemetido() {
        return SituacaoJulgamentoProcessoBusiness.isProcessoRemetido(
            this.registro?.tipoJulgamento,
            this.registro?.remessaJulgamento
        );
    }

    get isProcessoApreciado() {
        return SituacaoJulgamentoProcessoBusiness.isProcessoApreciado(this.registro?.resultadoJulgamento);
    }

    get isProcessoJulgado() {
        return SituacaoJulgamentoProcessoBusiness.isProcessoJulgado(this.registro?.resultadoJulgamento);
    }

    get titleVirtual() {
        return this.titleAlertaImpossibilidadeAlteracao;
    }

    get titlePresencial() {
        return this.titleAlertaImpossibilidadeAlteracao;
    }

    get titlePresencialRemetido() {
        return this.titleAlertaImpossibilidadeAlteracao;
    }

    get titleVirtualRemetido() {
        return this.titleAlertaImpossibilidadeAlteracao;
    }

    get titleAlertaImpossibilidadeAlteracao() {
        if (this.isSessaoFinalizada) {
            return this.msgImpossibilidadeAlteracaoEmSessaoFinalizada;
        }

        return '';
    }

    get msgImpossibilidadeAlteracaoEmSessaoFinalizada() {
        return 'Não é possível alterar o tipo de julgamento de processos em sessões já finalizadas.';
    }

    get carregarOpcaoTipoJulgamentoNaoVirtual() {
        let valor = '';
        let descricao = null;
        let desabilitado = false;

        switch (this.codigoMeioJulgamentoProcesso) {
            case MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo:
                valor = MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo;
                descricao = MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.descricaoCurta;
                desabilitado = this.isOpcaoPresencialDesabilitada;
                break;
            case MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo:
                // Fallback para registros com indicação de remessa errada no banco de dados
                if (this.isJulgamentoExclusivamenteVirtual) {
                    console.warn(
                        `Não deveria existir "${MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo}" com julgamento exclusivamente virtual [processo : ${this.registro.processo.numeroCompleto}]`
                    );
                    valor = MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.codigo;
                    descricao = MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.descricaoCurta;
                    desabilitado = this.isOpcaoPresencialRemetidoDesabilitada;
                } else {
                    valor = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo;
                    descricao = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.descricaoCurta;
                    desabilitado = this.isOpcaoPresencialRemetidoDesabilitada;
                }
                break;
            case MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo:
                if (this.isProcessoRemetido) {
                    if (this.isJulgamentoExclusivamenteVirtual) {
                        valor = MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.codigo;
                        descricao = MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.descricaoCurta;
                    } else {
                        valor = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo;
                        descricao = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.descricaoCurta;
                    }

                    desabilitado = this.isOpcaoVirtualRemetidoDesabilitada;
                } else if (this.isJulgamentoExclusivamenteVirtual) {
                    valor = MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.codigo;
                    descricao = 'Remeter para sessão futura';
                    desabilitado = this.isOpcaoVirtualDesabilitada;
                } else if (this.salaSessaoStore.podeAlterarTipoJulgamentoSemNecessidadeRemeter(this.registro)) {
                    valor = MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo;
                    descricao = (
                        <Stack
                            direction="row"
                            alignItems={'center'}
                        >
                            <Typography>{MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.descricaoCurta}</Typography>
                            {this.renderAlertaParaAlterarParaPresencial()}
                        </Stack>
                    );
                    desabilitado = this.isOpcaoPresencialDesabilitada;
                } else {
                    valor = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo;
                    descricao = 'Remeter para sessão presencial vinculada';
                    desabilitado = this.isOpcaoPresencialRemetidoDesabilitada;
                }
                break;
            default:
                break;
        }

        if (typeof descricao === 'string') {
            descricao = <Typography>{descricao}</Typography>;
        }

        return (
            <FormControlLabel
                value={valor}
                control={<Radio />}
                label={descricao}
                disabled={desabilitado}
            />
        );
    }

    carregarIcone() {
        let icone;
        let title;

        switch (this.codigoMeioJulgamentoProcesso) {
            case MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo:
                title = MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.descricao;

                if (this.codigoMeioJulgamentoSessao === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo) {
                    icone = this.renderIcone(
                        MEIO_JULGAMENTO_PROCESSO.PRESENCIAL,
                        true,
                        NIVEL_CRITICIDADE_ALERTA.INCONSISTENCIA
                    );
                } else {
                    icone = this.renderIcone(MEIO_JULGAMENTO_PROCESSO.PRESENCIAL);
                }
                break;
            case MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo:
                // Fallback para registros com indicação de remessa errada no banco de dados
                if (this.codigoMeioJulgamentoSessao === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo) {
                    /*
                    // TODO Decidir se será feito esse fallback (impacto nos filtros)
                    title = MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.descricao;
                    icone = this.renderIcone(MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO, true);
                    */

                    title = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.descricao;
                    icone = this.renderIcone(
                        MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA,
                        true,
                        NIVEL_CRITICIDADE_ALERTA.INCONSISTENCIA
                    );
                } else {
                    title = MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.descricao;
                    icone = this.renderIcone(MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA);
                }
                break;
            case MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo:
                if (this.isJulgamentoExclusivamenteVirtual && this.isProcessoRemetido) {
                    title = MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.descricao;

                    if (!this.salaSessaoStore.isProcessoJulgado(this.registro)) {
                        icone = this.renderIcone(MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO);
                    } else {
                        icone = this.renderIcone(
                            MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO,
                            true,
                            NIVEL_CRITICIDADE_ALERTA.INCONSISTENCIA
                        );
                    }
                } else {
                    title = MEIO_JULGAMENTO_PROCESSO.VIRTUAL.descricao;

                    if (this.sessaoSemPeriodoVirtualDefinido) {
                        icone = this.renderIcone(
                            MEIO_JULGAMENTO_PROCESSO.VIRTUAL,
                            true,
                            this.isProcessoEmPautaOuSessao
                                ? NIVEL_CRITICIDADE_ALERTA.INCONSISTENCIA
                                : NIVEL_CRITICIDADE_ALERTA.AVISO
                        );
                    } else {
                        icone = this.renderIcone(MEIO_JULGAMENTO_PROCESSO.VIRTUAL);
                    }
                }

                break;
            default:
                console.warn('Deveria retornar um meio de julgamento');
                icone = null;
                break;
        }

        return { icone, title };
    }

    handleClose = () => {
        this.acabouDeFechar = true;
        this.popoverAberto = false;
        this.openCadastroRemessa = false;
        this.anchorEl = null;

        setTimeout(() => {
            runInAction(() => {
                this.acabouDeFechar = false;
            });
        }, 10);
    };

    async handleClickIcone(event) {
        if (!this.acabouDeFechar && this.deveAbrirMenuAlteracao) {
            this.popoverAberto = true;
            this.anchorEl = event.currentTarget;

            if (this.exibirDetalhes) {
                const {
                    detalhesInconsistenciaJulgamentoPresencial,
                    detalhesInconsistenciaRemetidoPresencialVinculadaEmSessaoExclusivamenteVirtual,
                    detalhesInconsistenciaJulgamentoVirtual,
                    detalhesInconsistenciaRemetidoJulgadoEmSessaoExclusivamenteVirtual
                } = this.inconsistencias;

                runInAction(() => {
                    this.mensagensInconsistencias = [];
                });

                if (detalhesInconsistenciaJulgamentoPresencial) {
                    runInAction(() => {
                        this.mensagensInconsistencias.push(
                            MENSAGEM_INCOSISTENCIA.PRESENCIAL_EM_SESSAO_EXCLUSIVAMENTE_VIRTUAL
                        );
                    });
                }

                if (detalhesInconsistenciaRemetidoPresencialVinculadaEmSessaoExclusivamenteVirtual) {
                    runInAction(() => {
                        this.mensagensInconsistencias.push(
                            MENSAGEM_INCOSISTENCIA.REMETIDO_PARA_PRESENCIAL_VINCULADA_EM_SESSAO_EXCLUSIVAMENTE_VIRTUAL
                        );
                    });
                }

                if (detalhesInconsistenciaJulgamentoVirtual) {
                    runInAction(() => {
                        this.mensagensInconsistencias.push(
                            MENSAGEM_INCOSISTENCIA.VIRTUAL_EM_SESSAO_SEM_PERIODO_VIRTUAL
                        );
                    });
                }

                if (detalhesInconsistenciaRemetidoJulgadoEmSessaoExclusivamenteVirtual) {
                    runInAction(() => {
                        this.mensagensInconsistencias.push(MENSAGEM_INCOSISTENCIA.VIRTUAL_REMETIDO_JULGADO);
                    });
                }

                if (this.mensagensInconsistencias.length === 0 && HABILITAR_EXIBIR_DETALHES_REMESSA) {
                    await this.carregarMotivoRemessaPresencial();
                }
            }
        }
    }

    get inconsistencias() {
        const processoConfirmadoEmPautaSessao = this.salaSessaoStore.isProcessoConfirmadoEmPautaSessao(this.registro);

        const detalhesInconsistenciaJulgamentoPresencial =
            this.codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo &&
            this.isJulgamentoExclusivamenteVirtual &&
            processoConfirmadoEmPautaSessao;
        const detalhesInconsistenciaRemetidoPresencialVinculadaEmSessaoExclusivamenteVirtual =
            this.codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo &&
            this.isJulgamentoExclusivamenteVirtual;
        const detalhesInconsistenciaJulgamentoVirtual =
            this.codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo &&
            this.sessaoSemPeriodoVirtualDefinido &&
            processoConfirmadoEmPautaSessao;
        const detalhesInconsistenciaRemetidoJulgadoEmSessaoExclusivamenteVirtual =
            MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo && this.isProcessoRemetido && this.isProcessoJulgado;

        return {
            detalhesInconsistenciaJulgamentoPresencial,
            detalhesInconsistenciaRemetidoPresencialVinculadaEmSessaoExclusivamenteVirtual,
            detalhesInconsistenciaJulgamentoVirtual,
            detalhesInconsistenciaRemetidoJulgadoEmSessaoExclusivamenteVirtual
        };
    }

    async carregarMotivoRemessaPresencial() {
        const motivo = await this.processoService.recuperarMotivoRemessaPresencial(this.registro);
        const mensagem = [];

        if (motivo) {
            const formatoDataHoraEntrada = Configuracao.FORMATO_DATAHORA_SERVICOS_PADRAO;
            const formatoDataHoraSaida = 'DD/MM/YYYY HH:mm:ss';

            if (motivo.complemento) {
                mensagem.push(`Motivo: ${motivo.complemento}.`);
            }

            const dataHoraObj = dayjs(motivo.dataRemessa, formatoDataHoraEntrada);
            let dataHoraTxt;

            if (dataHoraObj) {
                dataHoraTxt = dataHoraObj.format(formatoDataHoraSaida);
            } else {
                dataHoraTxt = 'data/hora desconhecida';
            }

            if (motivo.autor) {
                mensagem.push(`${motivo.descricaoMotivo} ${motivo.autor}.`);
            } else {
                mensagem.push(`${motivo.descricaoMotivo}.`);
            }

            mensagem.push(`Data da Remessa: ${dataHoraTxt}.`);
        } else {
            mensagem.push('Motivo não cadastrado.');
        }

        runInAction(() => {
            this.motivoRemessaPresencial = mensagem;
        });
    }

    handleChangeTipoJulgamento = async (event) => {
        const novoTipo = event.target.value;

        if (novoTipo === MEIO_JULGAMENTO_PROCESSO.VIRTUAL_REMETIDO.codigo) {
            runInAction(() => {
                this.openCadastroRemessa = true;
                this.tipoRemessaPadrao = TIPO_REMESSA_JULGAMENTO_PROCESSO.RFP;
                this.popoverAberto = false;
            });
        } else if (
            (novoTipo === MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo ||
                novoTipo === MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo) &&
            !this.salaSessaoStore.podeAlterarTipoJulgamentoSemNecessidadeRemeter(this.registro)
        ) {
            runInAction(() => {
                this.openCadastroRemessa = true;
                this.tipoRemessaPadrao = TIPO_REMESSA_JULGAMENTO_PROCESSO.RP;
                this.popoverAberto = false;
            });
        } else {
            try {
                await this.processoService.alterarTipoJulgamento(this.registro, novoTipo);
                runInAction(() => {
                    this.registro.tipoJulgamento = novoTipo;
                });
            } catch (error) {
                console.error(error);
                this.mensagemStore.abrirMensagemErro(
                    `Ocorreu um erro ao tentar alterar o meio de julgamento do processo: ${ErrorUtil.montarMensagemErro(error)}`
                );
            } finally {
                runInAction(() => {
                    this.popoverAberto = false;
                });
            }
        }
    };

    renderAlertaParaAlterarParaPresencial() {
        if (!this.temAdvComParticipacaoVirtual) {
            return null;
        }

        return (
            <Tooltip
                title={
                    <Typography borderColor={'darkorange'}>
                        Atenção: há pelo menos um advogado cadastrado com modo de participação virtual!
                    </Typography>
                }
            >
                <Warning
                    sx={{
                        ml: 1,
                        color: 'darkorange'
                    }}
                />
            </Tooltip>
        );
    }

    render() {
        if (this.observableProps.apenasIcone) {
            return MeioJulgamentoProcesso.getIconeByCodigo(this.codigoMeioJulgamentoProcesso);
        }

        const { icone, title } = this.carregarIcone();

        if (!icone) {
            return null;
        }

        return (
            <>
                <Tooltip title={<Typography>{title + this.complementoTooltip}</Typography>}>{icone}</Tooltip>
                {this.popoverAberto && this.renderPopover()}
                {!this.isProcessoRemetido && this.openCadastroRemessa && (
                    <Popover
                        id={this.idPopoverCadastroRemessa}
                        open={this.openCadastroRemessa}
                        anchorEl={this.anchorEl}
                        onClose={this.handleClose}
                        sx={{
                            maxWidth: 'xl'
                        }}
                    >
                        <IconeTipoRemessaJulgamentoFormRemessa
                            registro={this.registro}
                            tipoRemessaPadrao={this.tipoRemessaPadrao}
                            onClose={this.handleClose}
                        />
                    </Popover>
                )}
            </>
        );
    }

    renderPopover() {
        if (this.isPermitidaAlteracao || this.isPermitidaRemessa) {
            return this.renderAlteracao();
        } else if (this.exibirDetalhes) {
            return this.renderDetalhes();
        }

        return null;
    }

    renderDetalhes() {
        if (this.mensagensInconsistencias.length > 0) {
            return (
                <PopoverSecretaria
                    open={this.popoverAberto}
                    anchorEl={this.anchorEl}
                    onClose={this.handleClose}
                    style={{ zIndex: 1350, maxWidth: '640px' }}
                >
                    <Typography
                        variant="h6"
                        color="primary"
                        sx={{ m: 1 }}
                    >
                        Não conformidade{this.mensagensInconsistencias.length > 1 ? 's' : ''} identificada
                        {this.mensagensInconsistencias.length > 1 ? 's' : ''}
                    </Typography>
                    <List>
                        {this.mensagensInconsistencias.map((mensagem) => (
                            <ListItem key={mensagem}>
                                <ListItemText primary={mensagem} />
                            </ListItem>
                        ))}
                    </List>
                </PopoverSecretaria>
            );
        } else {
            return (
                <PopoverSecretaria
                    open={this.popoverAberto}
                    anchorEl={this.anchorEl}
                    onClose={this.handleClose}
                    style={{ zIndex: 1350 }}
                >
                    <Typography
                        variant="h6"
                        color="primary"
                        sx={{ m: 1 }}
                    >
                        {this.isJulgamentoExclusivamenteVirtual
                            ? 'Remetido para sessão futura'
                            : 'Remetido para sessão presencial vinculada'}
                    </Typography>
                    {this.isJulgamentoExclusivamenteVirtual ? (
                        <DetalheRemessaJulgamento registro={this.registro} />
                    ) : (
                        <List dense>
                            {this.motivoRemessaPresencial.map((motivo) => (
                                <ListItem key={this.nextKey}>
                                    <Typography
                                        display={'inline'}
                                        sx={{ ml: 2 }}
                                    >
                                        {motivo}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </PopoverSecretaria>
            );
        }
    }

    get renderAlertaAlteracaoMeioJulgamento() {
        const restricoes = this.salaSessaoStore.verificarCondicoesParaAlteracaoMeioJulgamentoSatisfeitas(this.registro);

        if (restricoes.length === 0) {
            return null;
        }

        const tooltip = (
            <>
                <Typography>
                    Não é possível alterar o meio de julgamento do processo
                    {restricoes.length > 1 ? ' pelos seguintes motivos:' : ' pelo seguinte motivo:'}
                </Typography>
                <List dense={true}>
                    {restricoes.map((mensagem, index) => {
                        return (
                            <ListItem key={StringUtil.fnv1a32(mensagem)}>
                                <Typography>&#x25A1;</Typography>
                                <Typography
                                    display={'inline'}
                                    sx={{ ml: 1.5 }}
                                >
                                    {mensagem + (index === restricoes.length - 1 ? '.' : ';')}
                                </Typography>
                            </ListItem>
                        );
                    })}
                </List>
            </>
        );

        return (
            <Tooltip title={tooltip}>
                <Warning style={{ fill: 'darkorange', marginRight: '4px' }} />
            </Tooltip>
        );
    }

    get renderAlertaSessaoVirtualSemPeridoVirtualDefinido() {
        const isJulgamentoVirtualSemPeriodoVirtual = !!(
            this.codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo &&
            this.sessaoSemPeriodoVirtualDefinido
        );

        if (!isJulgamentoVirtualSemPeriodoVirtual) {
            return null;
        }

        return (
            <Tooltip
                placement={'top'}
                title={
                    <Typography borderColor={'darkorange'}>
                        {MENSAGEM_INCOSISTENCIA.VIRTUAL_EM_SESSAO_SEM_PERIODO_VIRTUAL}
                    </Typography>
                }
            >
                <Warning sx={{ color: 'darkorange', marginRight: '4px' }} />
            </Tooltip>
        );
    }

    renderAlteracao() {
        if (!this.popoverAberto) {
            return null;
        }

        return (
            <PopoverSecretaria
                open={this.popoverAberto}
                anchorEl={this.anchorEl}
                onClose={this.handleClose}
                style={{ zIndex: 1350 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        variant="h6"
                        color="primary"
                        sx={{ m: 1 }}
                    >
                        Meio de julgamento
                    </Typography>
                    {this.renderAlertaAlteracaoMeioJulgamento}
                    {this.renderAlertaSessaoVirtualSemPeridoVirtualDefinido}
                </Box>
                <FormControl>
                    <RadioGroup
                        name="tipoJulgamento"
                        value={this.codigoMeioJulgamentoProcessoForm}
                        onChange={this.handleChangeTipoJulgamento}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            ml: 2,
                            justifyContent: 'space-between'
                        }}
                    >
                        {this.carregarOpcaoTipoJulgamentoNaoVirtual}
                        <FormControlLabel
                            value={MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo}
                            control={<Radio />}
                            label={<Typography>{MEIO_JULGAMENTO_PROCESSO.VIRTUAL.descricaoCurta}</Typography>}
                            disabled={this.isOpcaoVirtualDesabilitada}
                            title={this.titleVirtual}
                        />
                    </RadioGroup>
                </FormControl>
            </PopoverSecretaria>
        );
    }

    /**
     * @param {Object} meioJulgamento
     * @param {boolean} [alerta=false]
     * @param {Object | null} [nivelCriticidadeAlerta=null]
     * @returns {JSX.Element}
     */
    renderIcone(meioJulgamento, alerta = false, nivelCriticidadeAlerta = null) {
        const cor = alerta ? deepOrange[600] : meioJulgamento.cor;
        const onClick = this.deveAbrirMenuAlteracao ? this.handleClickIcone.bind(this) : undefined;
        const cursor = this.deveAbrirMenuAlteracao ? 'pointer' : 'default';

        const estilo = {
            width: '26px',
            height: '26px',
            fontWeight: '600',
            fontSize: '0.96rem',
            backgroundColor: cor,
            cursor: cursor
        };

        if (alerta && nivelCriticidadeAlerta?.id === NIVEL_CRITICIDADE_ALERTA.INCONSISTENCIA.id) {
            return (
                <KeyframesBlinkTipoJulgamentoInconsistente>
                    <Avatar
                        onClick={onClick}
                        style={estilo}
                    >
                        {meioJulgamento.abreviacao}
                    </Avatar>
                </KeyframesBlinkTipoJulgamentoInconsistente>
            );
        }

        return (
            <Avatar
                onClick={onClick}
                style={estilo}
            >
                {meioJulgamento.abreviacao}
            </Avatar>
        );
    }
}

export default inject('stores')(observer(IconeTipoJulgamento));
